import React, { useState, useEffect, useRef, useCallback } from 'react';
import SuggestList from './SuggestList';
import {trackSearchBoxKeywordSuggest, trackSearchBoxLocationSuggest} from "@spa/features/search_job/pc/api/analytics/trackEvent";
import {useTrack} from "@spa/features/search_job/pc/contexts/TrackContext";
import {useSearch} from "@spa/features/search_job/pc/contexts/SearchContext";

const MAX_RETRY_COUNT = 2;

const SuggestInput = ({
                          id,
                          name,
                          inputValue,
                          maxLength,
                          type, // 'keyword' or 'location'
                          updateInputValue,
                          updateSelectedValue,
                          apiPath,
                          placeholder,
                          inputClassName,
                      }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [suggestions, setSuggestions] = useState([]);
    const [retryCount, setRetryCount] = useState(0);
    const [focusIndex, setFocusIndex] = useState(-1);
    const [isNavigation, setIsNavigation] = useState(false);
    const inputRef = useRef(null);
    const {channelName} = useTrack();
    const {setLocationSuggest} = useSearch();

    /**
     * 現在の入力値に基づいてサジェストをAPIから取得する
     */
    const fetchSuggestions = useCallback(async () => {
        if (isNavigation) return;
        // 入力値が空ならサジェストをクリア
        if (!inputValue || inputValue.trim() === '') {
            setSuggestions([]);
            if (type === 'location') {
                setLocationSuggest('');
            }
            return;
        }
        try {
            const response = await fetch(`${apiPath}?text=${encodeURIComponent(inputValue)}`);
            if (!response.ok) {
                throw new Error(`Failed to fetch suggestions: ${response.status}`);
            }
            const result = await response.json();
            const suggestionList = Array.isArray(result) ? result : result.data || [];
            setSuggestions(suggestionList || []);
            setRetryCount(0);

            // 勤務地のサジェストがある場合は最上位の値をセット
            if (type === 'location' && suggestionList.length > 0) {
                setLocationSuggest(suggestionList[0]);
            }
        } catch (error) {
            console.error(error);
            if (retryCount < MAX_RETRY_COUNT) {
                setRetryCount(prev => prev + 1);
                setTimeout(fetchSuggestions, 100);
            }
        }
    }, [inputValue, apiPath, retryCount, isNavigation]);

    useEffect(() => {
        fetchSuggestions();
    }, [inputValue, fetchSuggestions]);

    /**
     * 入力値変更時の処理
     */
    const handleInputChange = (e) => {
        setIsNavigation(false);
        updateInputValue(e);
    };

    /**
     * サジェスト選択時の処理
     * @param {string} selectedValue
     * @param {boolean} shouldSearch
     */
    const selectSuggestion = useCallback((selectedValue, shouldSearch = false) => {
        updateSelectedValue(selectedValue);
        setIsOpen(false);
        inputRef.current?.blur();
        if (shouldSearch && inputRef.current?.form) {
            inputRef.current.form.requestSubmit();
        }
    }, [updateSelectedValue]);

    /**
     * 入力フィールドのフォーカス時の処理
     */
    const handleInputFocus = () => {
        setIsOpen(true);
        setFocusIndex(-1);
    };

    /**
     * 入力フィールドのブラー時の処理
     */
    const handleInputBlur = () => {
        setTimeout(() => {
            setIsOpen(false);
            setFocusIndex(-1);
            setIsNavigation(false);
        }, 100);
    };

    /**
     * キーボード操作によるナビゲーション・選択処理
     */
    const handleKeyDown = (e) => {
        switch (e.key) {
            case 'Enter': {
                e.preventDefault();
                e.stopPropagation();
                if (suggestions.length > 0 && focusIndex !== -1) {
                    selectSuggestion(suggestions[focusIndex], true);
                } else {
                    inputRef.current.form.requestSubmit();
                }
                break;
            }
            case 'ArrowDown': {
                e.preventDefault();
                e.stopPropagation();
                setIsNavigation(true);
                const newIndex = (focusIndex === -1 || focusIndex >= suggestions.length - 1) ? 0 : focusIndex + 1;
                setFocusIndex(newIndex);
                updateSelectedValue(suggestions[newIndex]);
                break;
            }
            case 'ArrowUp': {
                e.preventDefault();
                e.stopPropagation();
                setIsNavigation(true);
                const newIndex = (focusIndex === -1 || focusIndex === 0) ? suggestions.length - 1 : focusIndex - 1;
                setFocusIndex(newIndex);
                updateSelectedValue(suggestions[newIndex]);
                break;
            }
            case 'Tab': {
                setIsOpen(false);
                break;
            }
        }
    };

    /**
     * サジェスト選択時のイベント
     */
    const handleSuggestionClick = (suggestion) => {
        selectSuggestion(suggestion, false);
        // 計測
        switch (type) {
            case 'keyword':
                trackSearchBoxKeywordSuggest(channelName);
                break;
            case 'location':
                trackSearchBoxLocationSuggest(channelName);
                break;
        }
    }

    return (
        <>
            <div className={`p-form p-headSearch--${type}`}>
                <span className="c-form_icon"></span>
                <input
                    ref={inputRef}
                    id={id}
                    name={name}
                    type="search"
                    value={inputValue || ''}
                    maxLength={maxLength}
                    onChange={handleInputChange}
                    onKeyDown={handleKeyDown}
                    onFocus={handleInputFocus}
                    onBlur={handleInputBlur}
                    placeholder={placeholder}
                    autoComplete="off"
                    className={`c-form_input${type === 'keyword' ? 'Kw' : 'Loc'} ${inputClassName}`}
                />
            </div>
            {isOpen && suggestions.length > 0 && (
                <SuggestList
                    suggestions={suggestions}
                    focusIndex={focusIndex}
                    onSuggestionClick={handleSuggestionClick}
                    type={type}
                />
            )}
        </>
    );
};

export default SuggestInput;
